// frontend/src/appsConfig.js
// App config for launcher and AppLayout: menu (sidebar) and routes (content) are relative to /app/:appId/

import React, { lazy } from "react";
import FundCommitmentsPage from "./pages/finance/fundCommitments/FundCommitmentsPage";
import FundDisbursementsPage from "./pages/finance/fundDisbursements/FundDisbursementsPage";
import EdmsSettingTabs from "./pages/edms/EdmsSettingTabs";
import EdmsFiling from "./pages/edms/EdmsFiling";
import EdmsArchive from "./pages/edms/EdmsArchive";
import EdmsDocuments from "./pages/edms/EdmsDocuments";
import EdmsFolders from "./pages/edms/EdmsFolders";

const MelDashboard = lazy(() => import('./pages/mel/MelDashboard'));
const MelLogframe = lazy(() => import('./pages/mel/MelLogframe'));
const MelActivityPlan = lazy(() => import('./pages/mel/MelActivityPlan'));
const MelIndicators = lazy(() => import('./pages/mel/MelIndicators'));
const MelDataSource = lazy(() => import('./pages/mel/MelDataSource'));
const MelRisks = lazy(() => import('./pages/mel/MelRisks'));
const ProjectsTabs = lazy(() => import('./pages/mel/ProjectsTabs'));
const RiskRegisterTabs = lazy(() => import('./pages/mel/RiskRegisterTabs'));
const CaseTrackerTabs = lazy(() => import('./pages/mel/CaseTrackerTabs'));
const AdvocacyLogTabs = lazy(() => import('./pages/mel/AdvocacyLogTabs'));
const MelDataDisaggregation = lazy(() => import('./pages/mel/MelDataDisaggregation'));
const RequistionDashboard = lazy(() => import('./pages/requisitions/RequistionDashboard'));
const RequisitionForecastDashboard = lazy(() => import('./pages/requisitions/RequisitionForecastDashboard'));
const ProcurementDashboard = lazy(() => import('./pages/procurements/ProcurementDashboard'));
const ProcurementPlanPage = lazy(() => import('./pages/procurements/ProcurementPlanPage'));
const ServiceProvidersPage = lazy(() => import('./pages/procurements/ServiceProvidersPage'));
const MailboxPage = lazy(() => import('./pages/procurements/MailboxPage'));
const FinanceDashboard = lazy(() => import('./pages/finance/FinanceDashboard'));
const BudgetAnalyticsPage = lazy(() => import('./pages/finance/analytics/BudgetAnalyticsPage'));
const FinancialPerformancePage = lazy(() => import('./pages/finance/analytics/FinancialPerformancePage'));
const FinancialProjectionPage = lazy(() => import('./pages/finance/analytics/FinancialProjectionPage'));
const ExpenditureAnalyticsPage = lazy(() => import('./pages/finance/analytics/ExpenditureAnalyticsPage'));
const VarianceAnalysisPage = lazy(() => import('./pages/finance/analytics/VarianceAnalysisPage'));
const FundFlowAnalyticsPage = lazy(() => import('./pages/finance/analytics/FundFlowAnalyticsPage'));
const FinanceCurrency = lazy(() => import('./pages/finance/currency/CurrencyPage'));
const FinanceBudget = lazy(() => import('./pages/finance/FinanceBudget'));
const FinanceExchangeRate = lazy(() => import('./pages/finance/exchangeRate/ExchangeRatePage'));
const CostCentersPage = lazy(() => import('./pages/finance/constCenter/FinanceCostCentersPage'));
const FinanceAccountCodesPage = lazy(() => import('./pages/finance/accountCode/FinanceAccountCodesPage'));
const ContractsPage = lazy(() => import('./pages/finance/contracts/ContractsPage'));
const MembersPartnersDashboard = lazy(() => import('./pages/membersPartners/MembersPartnersDashboard'));
const MembersPartnersEntities = lazy(() => import('./pages/membersPartners/MembersPartnersEntities'));
const MembersPartnersClubs = lazy(() => import('./pages/membersPartners/MembersPartnersClubs'));
const MembersPartnersParalegalCenters = lazy(() => import('./pages/membersPartners/MembersPartnersParalegalCenters'));
const MembersPartnersMembers = lazy(() => import('./pages/membersPartners/MembersPartnersMembers'));
const MembersPartnersFounders = lazy(() => import('./pages/membersPartners/MembersPartnersFounders'));
const MembersPartnersPartnerEntities = lazy(() => import('./pages/membersPartners/MembersPartnersPartnerEntities'));
const MembersPartnersFriendsOfHaki = lazy(() => import('./pages/membersPartners/MembersPartnersFriendsOfHaki'));
const MembersPartnersParticipants = lazy(() => import('./pages/membersPartners/MembersPartnersParticipants'));
const MembersPartnersResourcePersons = lazy(() => import('./pages/membersPartners/MembersPartnersResourcePersons'));
const MembersPartnersAlumni = lazy(() => import('./pages/membersPartners/MembersPartnersAlumni'));
const MembersPartnersMemberContributions = lazy(() => import('./pages/membersPartners/MembersPartnersMemberContributions'));
const MembersPartnersStakeholderView = lazy(() => import('./pages/membersPartners/MembersPartnersStakeholderView'));
const EventsDashboard = lazy(() => import('./pages/events/EventsDashboard'));
const EventsManagement = lazy(() => import('./pages/events/EventsManagementPage'));
const EventDetailsPage = lazy(() => import('./pages/events/EventDetailsPage'));
const EventCalendarImportReviewPage = lazy(() => import('./pages/events/EventCalendarImportReviewPage'));
const ReportsHome = lazy(() => import('./pages/reports/ReportsHome'));
const AssetDashboard = lazy(() => import('./pages/asset/AssetDashboard'));
const PpeDashboard = lazy(() => import('./pages/asset/PpeDashboard'));
const AssetSettings = lazy(() => import('./pages/asset/AssetSettings'));
const AssetRegisterPage = lazy(() => import('./pages/asset/AssetRegisterPage'));
const FarRegisterPage = lazy(() => import('./pages/asset/FarRegisterPage'));
const AssetGroupsPage = lazy(() => import('./pages/asset/AssetGroupsPage'));
const AssetAllocationPage = lazy(() => import('./pages/asset/AssetAllocationPage'));
const AssetMovementPage = lazy(() => import('./pages/asset/AssetMovementPage'));
const AssetAuditPage = lazy(() => import('./pages/asset/AssetAuditPage'));
const AssetDisposalPage = lazy(() => import('./pages/asset/AssetDisposalPage'));
const AssetReportsPage = lazy(() => import('./pages/asset/AssetReportsPage'));
const SelfServiceDashboard = lazy(() => import('./pages/selfService/SelfServiceDashboard'));
const SelfServiceSectionPage = lazy(() => import('./pages/selfService/SelfServiceSectionPage'));
const NotificationsPage = lazy(() => import('./pages/selfService/NotificationsPage'));
const SelfServicePerformanceOverview = lazy(() => import('./pages/selfService/performance/SelfServicePerformanceOverview'));
const SelfServiceSmartPlanPage = lazy(() => import('./pages/selfService/performance/SelfServiceSmartPlanPage'));
const SelfServiceLeavesOverview = lazy(() => import('./pages/selfService/leaves/SelfServiceLeavesOverview'));
const SelfServiceLeavesHistory = lazy(() => import('./pages/selfService/leaves/SelfServiceLeavesHistory'));
const HrmDashboard = lazy(() => import('./pages/hrm/HrmDashboard'));
const PerformanceManagementDashboard = lazy(() => import('./pages/hrm/PerformanceManagementDashboard'));
const MelForms = lazy(() => import('./pages/mel/MelForms'));
const ResearchFormsPage = lazy(() => import('./pages/mel/ResearchFormsPage'));
const MelResearchSettingsPage = lazy(() => import('./pages/mel/MelResearchSettingsPage'));
const ActivityTracker = lazy(() => import('./pages/mel/ActivityTracker'));
const IndicatorTracker = lazy(() => import('./pages/mel/IndicatorTracker'));
const FormsConfigurations = lazy(() => import('./pages/adm/FormsConfigurations'));

const EmployeeProfilesPage = lazy(() => import('./pages/hrm/EmployeeProfilesPage'));
const EmployeeProfileDetailsPage = lazy(() => import('./pages/hrm/EmployeeProfileDetailsPage'));
const EmployeeContractPage = lazy(() => import('./pages/hrm/EmployeeContract'));
const EmployeeDependantsPage = lazy(() => import('./pages/hrm/EmployeeDependantsPage'));
const EmployeeFilesPage = lazy(() => import('./pages/hrm/EmployeeFilesPage'));
const LeavePlanningPage = lazy(() => import('./pages/hrm/LeavePlanningPage'));
const LeaveManagementPage = lazy(() => import('./pages/hrm/LeaveManagementPage'));
const PerformanceGoalsPage = lazy(() => import('./pages/hrm/PerformanceGoalsPage'));
const EvaluationSetupPage = lazy(() => import('./pages/hrm/EvaluationSetupPage'));
const EvaluationsPage = lazy(() => import('./pages/hrm/EvaluationsPage'));
const EvaluationSmartPlanPage = lazy(() => import('./pages/hrm/evaluations/EvaluationSmartPlanPage'));
const EvaluationPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationPrintPage'));
const EvaluationSmartPlanPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationSmartPlanPrintPage'));
const EvaluationMonthlyCheckinPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationMonthlyCheckinPrintPage'));
const EvaluationAppraisalFormPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationAppraisalFormPrintPage'));
const EvaluationPipFormPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationPipFormPrintPage'));
const Evaluation360FeedbackPrintPage = lazy(() => import('./pages/hrm/evaluations/Evaluation360FeedbackPrintPage'));
const EvaluationAppealFormPrintPage = lazy(() => import('./pages/hrm/evaluations/EvaluationAppealFormPrintPage'));
const TrainingPlanningPage = lazy(() => import('./pages/hrm/TrainingPlanningPage'));
const PromotionsPage = lazy(() => import('./pages/hrm/PromotionsPage'));
const ResignationsPage = lazy(() => import('./pages/hrm/ResignationsPage'));
const TerminationsPage = lazy(() => import('./pages/hrm/TerminationsPage'));
const ContractsPageHrm = lazy(() => import('./pages/hrm/ContractsPage'));
const NewContractPage = lazy(() => import('./pages/hrm/NewContractPage'));

const EdmsDashboard = lazy(() => import('./pages/edms/EdmsDashboard'));

const InternalMemoPage = lazy(
  () => import("./pages/requisitions/InternalMemoPage"),
);
const ImprestPage = lazy(() => import("./pages/requisitions/ImprestPage"));
const ImprestRetirementPage = lazy(
  () => import("./pages/requisitions/ImprestRetirementPage"),
);
const PettyCashPage = lazy(() => import("./pages/requisitions/PettyCashPage"));
const PettyCashHQPage = lazy(() => import("./pages/requisitions/PettyCashHQPage/PettyCashHQPage"));
const PettyCashDodomaPage = lazy(() => import("./pages/requisitions/PettyCashDodomaPage/PettyCashDodomaPage"));
const PettyCashKinondoniPage = lazy(() => import("./pages/requisitions/PettyCashKinondoniPage/PettyCashKinondoniPage"));
const PettyCashArushaPage = lazy(() => import("./pages/requisitions/PettyCashArushaPage/PettyCashArushaPage"));
const PaymentVoucherPage = lazy(
  () => import("./pages/requisitions/PaymentVoucherPage"),
);
const ClaimPage = lazy(() => import("./pages/requisitions/ClaimPage"));
const InvoicePage = lazy(() => import("./pages/requisitions/InvoicePage"));
const IncomingMailPage = lazy(() => import("./pages/requisitions/IncomingMailPage"));
const NormalMailPage = lazy(() => import("./pages/requisitions/NormalMailPage"));
const JobApplicationPage = lazy(() => import("./pages/requisitions/JobApplicationPage"));
const InvoiceMailPage = lazy(() => import("./pages/requisitions/InvoiceMailPage"));
const VolunteerAllowancePage = lazy(
  () => import("./pages/requisitions/VolunteerAllowancePage")
);
const ActivityReportPage = lazy(() => import("./pages/requisitions/ActivityReportPage"));
const MyRetirementsPage = lazy(() => import("./pages/requisitions/MyRetirementsPage"));
const PurchaseRequisitionPage = lazy(
  () => import("./pages/requisitions/PurchaseRequisitionPage")
);
const PaymentsRequisitionListPage = lazy(
  () => import("./pages/requisitions/PaymentsRequisitionListPage")
);
const PaymentVoucherListPage = lazy(
  () => import("./pages/requisitions/PaymentVoucherListPage")
);
const ApprovedPettyCashVoucherQueuePage = lazy(
  () => import("./pages/requisitions/ApprovedPettyCashVoucherQueuePage")
);
const ProcurementMemoPage = lazy(
  () => import("./pages/requisitions/ProcurementMemoPage")
);
const WorkflowRepositoryPage = lazy(
  () => import("./pages/requisitions/WorkflowRepositoryPage")
);
const MyRequisitionPage = lazy(() => import("./pages/requisitions/MyRequisitionPage"));
const TasksPage = lazy(() => import("./pages/requisitions/TasksPage"));
const AssignmentViewPage = lazy(() => import("./pages/requisitions/AssignmentViewPage"));

// Administration modules
const WorkflowSettingsTabs = lazy(
  () => import("./pages/adm/WorkflowSettingsTabs"),
);
const ProjectSettingsTabs = lazy(
  () => import("./pages/adm/ProjectsSettingsTabs"),
);
const RisksSettingsTabs = lazy(() => import("./pages/adm/RisksSettingsTabs"));

const IndicatorSettingsTabs = lazy(
  () => import("./pages/adm/IndicatorsSettingsTabs"),
);
const FinanceSettingsTabs = lazy(
  () => import("./pages/adm/FinanceSettingsTabs"),
);
const DemographicsSettingsTabs = lazy(
  () => import("./pages/adm/DemographicsSettingsTabs"),
);
const PersonaSettingsTabs = lazy(
  () => import("./pages/adm/PersonaSettingsTabs"),
);
const GeneralSettingsTabs = lazy(
  () => import("./pages/adm/GeneralSettingsTabs"),
);
const MembersSettingsTabs = lazy(
  () => import("./pages/adm/MembersSettingsTabs"),
);

const ResearchSettingsTabs = lazy(
  () => import("./pages/adm/ResearchSettingsTabs"),
);
const MediaSettingsTabs = lazy(() => import("./pages/adm/MediaSettingsTabs"));
const FeedbackSettingsTabs = lazy(
  () => import("./pages/adm/FeedbackSettingsTabs"),
);
const UsersManagementTabs = lazy(
  () => import("./pages/adm/UsersManagementTabs"),
);

const EdmsSettingsTabs = lazy(
  () => import("./pages/adm/EdmsSettingsTabs"),
);

const ComplianceDashboard = lazy(() => import('./pages/compliance/ComplianceDashboard'));
const ComplianceSectionPage = lazy(() => import('./pages/compliance/ComplianceSectionPage'));
const ProcurementForms = lazy(() => import('./pages/procurements/ProcurementForms'));

const MembersPartnersRadios = lazy(() => import('./pages/membersPartners/MembersPartnersRadios'));

const AuditLog = lazy(() => import('./pages/adm/AuditLog'));
const FinanceForms = lazy(() => import('./pages/finance/FinanceForms'));

// DocSign
const DocsignDashboard = lazy(() => import('./pages/docsign/DocsignDashboard'));
const DocsignTasks = lazy(() => import('./pages/docsign/DocsignTasks'));
const DocsignUpload = lazy(() => import('./pages/docsign/DocsignUpload'));
const DocsignRepository = lazy(() => import('./pages/docsign/DocsignRepository'));
const DocsignHistory = lazy(() => import('./pages/docsign/DocsignHistory'));
const DocsignSettings = lazy(() => import('./pages/docsign/DocsignSettings'));

// Helpdesk
const AdminHelpdeskSettingsPage = lazy(() => import('./pages/helpdesk/AdminHelpdeskSettingsPage'));
const HelpDeskDashboard = lazy(() => import('./pages/helpdesk/HelpDeskDashboard'));
const HelpdeskTicketsPage = lazy(() => import('./components/helpdesk/tickets/TicketsPage'));

const MyDiaryDashboard = lazy(() => import('./pages/diary/MyDiaryDashboard'));
const MyDiaryPage = lazy(() => import('./pages/diary/MyDiaryPage'));
const StaffDiariesPage = lazy(() => import('./pages/diary/StaffDiariesPage'));
const MeetingPage = lazy(() => import('./pages/diary/MeetingPage'));
const MinutesToolPage = lazy(() => import('./pages/diary/MinutesToolPage'));
const OrganogramPage = lazy(() => import('./pages/hrm/OrganogramPage'));
const PositionsPage = lazy(() => import('./pages/hrm/PositionsPage'));
const HRSettingsTab = lazy(() => import('./pages/hrm/HRSettingsTab'));
const HRLeaveSettingsTab = lazy(() => import('./pages/hrm/HRLeaveSettingsTab'));
const HRPerformanceManagementSettingsTab = lazy(() => import('./pages/hrm/HRPerformanceManagementSettingsTab'));
const EmployeeMissingDocumentsReportPage = lazy(() => import('./pages/hrm/EmployeeMissingDocumentsReportPage'));
const UserProfilesPage = lazy(() => import('./pages/hrm/UserProfilesPage'));
const UserProfileDetailsPage = lazy(() => import('./pages/hrm/UserProfileDetailsPage'));
const MyProfilePage = lazy(() => import('./pages/hrm/MyProfilePage'));

/** Lazy import getters for global route pages (login, not-found, etc.). Paths defined here only. */
export const ROUTE_PAGES = {
  LoginPage: () => import("./pages/shared/LoginPage"),
  NotFoundPage: () => import("./pages/shared/NotFoundPage"),
  LegacyDashboardRedirectPage: () =>
    import("./pages/shared/LegacyDashboardRedirectPage"),
  ForcePasswordChangePage: () =>
    import("./pages/shared/ForcePasswordChangePage"),
  VerifyOtpPage: () => import("./pages/shared/VerifyOtpPage"),
  UnauthorizedPage: () => import("./pages/shared/UnauthorizedPage"),
};

/** Sync export for NotFoundPage (used by AppLayout). */
export { default as NotFoundPage } from "./pages/shared/NotFoundPage";

export const APPS = {
  mel: {
    id: "mel",
    name: "MEL",
    description: "Monitoring & Evaluation",
    menu: [
      { type: "section", label: "Overview" },
      { label: "Activity Analytics", to: "", icon: "home" },
      { label: "Projects", to: "projects", icon: "square-dashed-kanban" },
      { label: "Indicators", to: "indicators", icon: "chart" },
      { label: "Activity tracker", to: "activity-tracker", icon: "folder" },
      { label: "Indicator tracker", to: "indicator-tracker", icon: "chart" },
      { label: "Data Source", to: "data-sources", icon: "folder" },
      { label: "Risks", to: "risks", icon: "clock" },
      { label: "LogFrame", to: "logframe", icon: "chart" },
      { label: "Activity Plan", to: "activity", icon: "folder" },
      { label: "Risk Register", to: "risk-register", icon: "clock" },
      { label: "Case Tracker", to: "case-tracker", icon: "folder" },
      { label: "Advocacy Log", to: "advocacy-log", icon: "folder" },
      { label: "MEL Forms", to: "mel-forms", icon: "folder" },
      { label: "Research Forms", to: "research-forms", icon: "folder" },
      { label: "Research Settings", to: "research-settings", icon: "settings" },
    ],
    routes: [
      { index: true, element: <MelDashboard /> },
      { path: "dashboard", element: <MelDashboard /> },
      { path: "projects", element: <ProjectsTabs /> },
      { path: "indicators", element: <MelIndicators /> },
      { path: "activity-tracker", element: <ActivityTracker /> },
      { path: "indicator-tracker", element: <IndicatorTracker /> },
      { path: "data-sources", element: <MelDataSource /> },
      { path: "risks", element: <MelRisks /> },
      { path: "logframe", element: <MelLogframe /> },
      { path: "activity", element: <MelActivityPlan /> },
      { path: "risk-register", element: <RiskRegisterTabs /> },
      { path: "case-tracker", element: <CaseTrackerTabs /> },
      { path: "advocacy-log", element: <AdvocacyLogTabs /> },
      { path: "mel-forms", element: <MelForms /> },
      { path: "research-forms", element: <ResearchFormsPage /> },
      { path: "research-settings", element: <MelResearchSettingsPage /> },
    ],
  },

  requisitions: {
    id: "requisitions",
    name: "Requisitions",
    description: "Requisitions",
    menu: [
      { type: "section", label: "Overview" },
      { label: "Dashboard", to: "", icon: "home" },
      { label: "Requisition Forecast", to: "requisition-forecast", icon: "chart" },

      { type: "section", label: "My work" },
      { label: "Tasks", to: "tasks", icon: "inbox-tasks" },
      { label: "Assignment View", to: "assignment-view", icon: "user-plus" },

      { type: "section", label: "New requests" },
      { label: "Internal Memo", to: "internal-memo", icon: "scroll-text" },
      { label: "Imprest Request", to: "imprest", icon: "disbursement" },
      { label: "Petty Cash (HQ)", to: "petty-cash-hq", icon: "building" },
      { label: "Petty Cash (Arusha)", to: "petty-cash-arusha", icon: "map-pinned" },
      { label: "Petty Cash (Kinondoni)", to: "petty-cash-kinondoni", icon: "map-pin" },
      { label: "Petty Cash (Dodoma)", to: "petty-cash-dodoma", icon: "earth" },
      { label: "Claim", to: "claim", icon: "request" },
      /* Job application / invoice incoming mail: routes exist for deep links; entry is the dialog (type pick), not the menu. */
      { label: "Incoming Mails", to: "incoming-mails", icon: "inbox", openInDialog: true },
      { label: "Volunteer Allowance", to: "volunteer-allowance", icon: "heart" },
      { label: "Procurement Memo", to: "procurement-memo", icon: "package" },

      { type: "section", label: "My records" },
      { label: "My Requisitions", to: "my-requisitions", icon: "my-requisitions" },
      { label: "My Retirements", to: "my-retirements", icon: "contract" },

      { type: "section", label: "Team queues" },
      { label: "Procurement Queue", to: "procurement-requisitions", icon: "square-dashed-kanban" },
      { label: "Payment Approval Queue", to: "payments-requisitions", icon: "landmark" },
      {
        label: "Petty Cash",
        to: "approved-petty-cash-vouchers",
        icon: "wallet",
      },
      { label: "Payment Vouchers", to: "payment-vouchers", icon: "banknote" },

      { type: "section", label: "Tools" },
      { label: "Workflow Repository", to: "workflow-repository", icon: "database" },
    ],
    routes: [
      { index: true, element: <RequistionDashboard /> },
      { path: "dashboard", element: <RequistionDashboard /> },
      { path: "requisition-forecast", element: <RequisitionForecastDashboard /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "internal-memo", element: <InternalMemoPage /> },
      { path: "internal-memo/:id", element: <InternalMemoPage /> },
      { path: "imprest", element: <ImprestPage /> },
      { path: "imprest/:id", element: <ImprestPage /> },
      { path: "retirement", element: <ImprestRetirementPage /> },
      { path: "retirement/:id", element: <ImprestRetirementPage /> },
      { path: "petty-cash-hq", element: <PettyCashHQPage /> },
      { path: "petty-cash-hq/:id", element: <PettyCashHQPage /> },
      { path: "petty-cash-arusha", element: <PettyCashArushaPage /> },
      { path: "petty-cash-arusha/:id", element: <PettyCashArushaPage /> },
      { path: "petty-cash-kinondoni", element: <PettyCashKinondoniPage /> },
      { path: "petty-cash-kinondoni/:id", element: <PettyCashKinondoniPage /> },
      { path: "petty-cash-dodoma", element: <PettyCashDodomaPage /> },
      { path: "petty-cash-dodoma/:id", element: <PettyCashDodomaPage /> },
      { path: "claim", element: <ClaimPage /> },
      { path: "claim/:id", element: <ClaimPage /> },
      { path: "invoices", element: <InvoicePage /> },
      { path: "invoices/:id", element: <InvoicePage /> },
      // Backward-compatible routes (older links)
      { path: "invoice-workflow", element: <InvoicePage /> },
      { path: "invoice-workflow/:id", element: <InvoicePage /> },
      { path: "normal-mail", element: <NormalMailPage /> },
      { path: "normal-mail/:id", element: <NormalMailPage /> },
      { path: "invoice-mail", element: <InvoiceMailPage /> },
      { path: "invoice-mail/:id", element: <InvoiceMailPage /> },
      { path: "incoming-mails", element: <IncomingMailPage /> },
      { path: "incoming-mails/:id", element: <IncomingMailPage /> },
      { path: "job-applications", element: <JobApplicationPage /> },
      { path: "job-applications/:id", element: <JobApplicationPage /> },
      { path: "volunteer-allowance", element: <VolunteerAllowancePage /> },
      { path: "volunteer-allowance/:id", element: <VolunteerAllowancePage /> },
      { path: "procurement-memo", element: <ProcurementMemoPage /> },
      { path: "procurement-memo/create", element: <ProcurementMemoPage /> },
      { path: "procurement-memo/:id", element: <ProcurementMemoPage /> },
      { path: "activity-report", element: <ActivityReportPage /> },
      { path: "activity-report/:id", element: <ActivityReportPage /> },
      { path: "procurement-requisitions", element: <PurchaseRequisitionPage /> },
      { path: "procurement-requisitions/:id", element: <PurchaseRequisitionPage /> },
      { path: "payments-requisitions", element: <PaymentsRequisitionListPage /> },
      { path: "payments-requisitions/:id", element: <PaymentsRequisitionListPage /> },
      {
        path: "approved-petty-cash-vouchers",
        element: <ApprovedPettyCashVoucherQueuePage />,
      },
      { path: "payment-vouchers", element: <PaymentVoucherListPage /> },
      { path: "payment-voucher", element: <PaymentVoucherPage /> },
      { path: "payment-voucher/:id", element: <PaymentVoucherPage /> },
      { path: "payment-requisition/create", element: <PaymentVoucherPage /> },
      { path: "my-requisitions", element: <MyRequisitionPage /> },
      { path: "my-retirements", element: <MyRetirementsPage /> },
      { path: "workflow-repository", element: <WorkflowRepositoryPage /> },
      { path: "repository", element: <MyRequisitionPage /> },
      { path: "assignment-view", element: <AssignmentViewPage /> },
    ],
  },

  procurements: {
    id: "procurements",
    name: "Procurements",
    description: "Procurements",
    menu: [
      { label: "Dashboard", to: "", icon: "home" },
      { label: "Procurement Plan", to: "procurement-plan", icon: "chart" },
      { label: "Service Providers", to: "service-providers", icon: "users" },
      { label: "Mailbox", to: "mailbox", icon: "email" },
      { label: "Procurement Forms", to: "procurement-forms", icon: "forms" },
    ],
    routes: [
      { index: true, element: <ProcurementDashboard /> },
      { path: "dashboard", element: <ProcurementDashboard /> },
      { path: "procurement-plan", element: <ProcurementPlanPage /> },
      { path: "service-providers", element: <ServiceProvidersPage /> },
      { path: "mailbox", element: <MailboxPage /> },
      { path: "procurement-forms", element: <ProcurementForms /> },
      
    ],
  },

  finance: {
    id: "finance",
    name: "Finance",
    description: "Finance",
    menu: [
      // { label: "Dashboard", to: "", icon: "home" },
      
     
      { type: "section", label: "Financial analytics" },
      { label: "Budget Analytics", to: "analytics/budget-analytics", icon: "budget" },
      { label: "Financial Projections", to: "analytics/financial-projections", icon: "gauge" },
      { label: "Financial Performance", to: "analytics/financial-performance", icon: "trending-up" },
      { label: "Expenditure Analytics", to: "analytics/expenditure-analytics", icon: "banknote" },
      { label: "Variance Analysis", to: "analytics/variance-analysis", icon: "file-stack" },
      { label: "Fund Flow Analytics", to: "analytics/fund-flow-analytics", icon: "workflow" },
      // { label: 'Currency', to: 'currency', icon: 'wallet' },
      { type: "section", label: "Budgeting" },
      { label: "Budget Items", to: "budget", icon: "budget" },
      { label: "Cost Centers", to: "cost-centers", icon: "cost-center" },
      { label: "Account Codes", to: "account-codes", icon: "file-stack" },
      { type: "section", label: "Funding" },
      { label: "Fund Commitments", to: "fund-commitments", icon: "commitment" },
      {
        label: "Fund Disbursement",
        to: "fund-disbursement",
        icon: "disbursement",
      },
      { label: "Contracts", to: "contracts", icon: "contract" },
      { type: "section", label: "Modelling" },
      { label: "Exchange Rates", to: "exchange-rate", icon: "exchange-rate" },
      { label: "Finance Forms", to: "finance-forms", icon: "forms"}
    ],
    routes: [
      { index: true, element: <BudgetAnalyticsPage /> },
      // { path: "dashboard", element: <FinanceDashboard /> },
      { path: "analytics/budget-analytics", element: <BudgetAnalyticsPage /> },
      { path: "analytics/financial-projections", element: <FinancialProjectionPage /> },
      { path: "analytics/financial-performance", element: <FinancialPerformancePage /> },
      { path: "analytics/expenditure-analytics", element: <ExpenditureAnalyticsPage /> },
      { path: "analytics/variance-analysis", element: <VarianceAnalysisPage /> },
      { path: "analytics/fund-flow-analytics", element: <FundFlowAnalyticsPage /> },
      // { path: 'currency', element: <FinanceCurrency /> },
      { path: "budget", element: <FinanceBudget /> },
      { path: "exchange-rate", element: <FinanceExchangeRate /> },
      { path: "cost-centers", element: <CostCentersPage /> },
      { path: "account-codes", element: <FinanceAccountCodesPage /> },
      { path: "fund-commitments", element: <FundCommitmentsPage /> },
      { path: "fund-disbursement", element: <FundDisbursementsPage /> },
      { path: "contracts", element: <ContractsPage /> },
      { path: "finance-forms", element: <FinanceForms />}
    ],
  },

  docsign: {
    id: 'docsign',
    name: 'DocSign',
    description: 'Document signing engine',
    menu: [
      { label: 'Dashboard', to: '', icon: 'home' },
      { label: 'Tasks', to: 'tasks', icon: 'check-circle' },
      { label: 'Upload', to: 'upload', icon: 'plus' },
      { label: 'Repository', to: 'repository', icon: 'database' },
      { label: 'Signing History', to: 'history', icon: 'clock' },
      { label: 'Settings', to: 'settings', icon: 'settings' },
    ],
    routes: [
      { index: true, element: <DocsignDashboard /> },
      { path: 'dashboard', element: <DocsignDashboard /> },
      { path: 'tasks', element: <DocsignTasks /> },
      { path: 'upload', element: <DocsignUpload /> },
      { path: 'repository', element: <DocsignRepository /> },
      { path: 'history', element: <DocsignHistory /> },
      { path: 'settings', element: <DocsignSettings /> },
    ],
  },

  'members-partner': {
    id: 'members-partner',
    name: 'Our StakeHolders',
    description: 'Our StakeHolders',
    menu: [
      { label: 'Dashboard', to: '', icon: 'home' },
      { label: 'Human Rights Clubs', to: 'clubs', icon: 'users-round' },
      { label: 'Paralegal Units', to: 'paralegal-centers', icon: 'users-round' },
      { label: 'LHRC Members', to: 'members', icon: 'users' },
      { label: 'LHRC Founders', to: 'founders', icon: 'users' },
      { label: 'Development Partners', to: 'partners', icon: 'users' },
      { label: 'Activity Participants', to: 'participants', icon: 'network' },
      { label: 'Resource Persons', to: 'resource-persons', icon: 'users-round' },
      { label: 'LHRC Alumni', to: 'alumni', icon: 'users-round' },
      { label: 'Member Contributions', to: 'member-contributions', icon: 'wallet' },
      { label: 'Friends of Haki', to: 'friends-of-haki', icon: 'group' },
      { label: 'Media', to: 'media', icon: 'radio' },
      { label: 'Other Entities', to: 'entities', icon: 'database' },
    ],
    routes: [
      { index: true, element: <MembersPartnersDashboard /> },
      { path: 'dashboard', element: <MembersPartnersDashboard /> },
      { path: 'entities', element: <MembersPartnersEntities /> },
      { path: 'clubs', element: <MembersPartnersClubs /> },
      { path: 'paralegal-centers', element: <MembersPartnersParalegalCenters /> },
      { path: 'members', element: <MembersPartnersMembers /> },
      { path: 'founders', element: <MembersPartnersFounders /> },
      { path: 'partners', element: <MembersPartnersPartnerEntities /> },
      { path: 'participants', element: <MembersPartnersParticipants /> },
      { path: 'resource-persons', element: <MembersPartnersResourcePersons /> },
      { path: 'alumni', element: <MembersPartnersAlumni /> },
      { path: 'member-contributions', element: <MembersPartnersMemberContributions /> },
      { path: 'view/:section/:id', element: <MembersPartnersStakeholderView /> },
      { path: 'friends-of-haki', element: <MembersPartnersFriendsOfHaki /> },
      { path: 'media', element: <MembersPartnersRadios /> },
      { path: 'radios', element: <MembersPartnersRadios /> },
    ],
  },

  events: {
    id: 'events',
    name: 'Events',
    description: 'Events',
    menu: [
      { label: 'Dashboard', to: '', icon: 'home' },
      { label: 'Event Management', to: 'management', icon: 'clock' },
    ],
    routes: [
      { index: true, element: <EventsDashboard /> },
      { path: 'dashboard', element: <EventsDashboard /> },
      { path: 'management', element: <EventsManagement /> },
      { path: 'management/import/review', element: <EventCalendarImportReviewPage /> },
      { path: 'management/:eventId', element: <EventDetailsPage /> },
    ],
  },

  reports: {
    id: "reports",
    name: "Reports",
    description: "Reports",
    menu: [{ label: "Dashboard", to: "", icon: "home" }],
    routes: [{ index: true, element: <ReportsHome /> }],
  },

  assets: {
    id: "assets",
    name: "Assets",
    description: "Assets",
    menu: [
      { label: "Dashboard", to: "", icon: "home" },
      { label: "PPE", to: "ppe", icon: "landmark" },
      { label: "Assets", to: "register", icon: "package" },
      { label: "Asset Groups", to: "groups", icon: "folders" },
      { label: "FAR", to: "far", icon: "file-spreadsheet" },
      { label: "Allocation", to: "allocation", icon: "users" },
      { label: "Movement", to: "movement", icon: "workflow" },
      { label: "Audit", to: "audit", icon: "clock" },
      { label: "Disposal", to: "disposal", icon: "folder" },
      { label: "Reports", to: "reports", icon: "chart" },
      { label: "Settings", to: "settings", icon: "settings" },
    ],
    routes: [
      { index: true, element: <AssetDashboard /> },
      { path: "ppe", element: <PpeDashboard /> },
      { path: "register", element: <AssetRegisterPage /> },
      { path: "far", element: <FarRegisterPage /> },
      { path: "groups", element: <AssetGroupsPage /> },
      { path: "allocation", element: <AssetAllocationPage /> },
      { path: "movement", element: <AssetMovementPage /> },
      { path: "audit", element: <AssetAuditPage /> },
      { path: "disposal", element: <AssetDisposalPage /> },
      { path: "reports", element: <AssetReportsPage /> },
      { path: "settings", element: <AssetSettings /> },
    ],
  },

  "self-service": {
    id: "self-service",
    name: "Self Service",
    description: "Self Service",
    menu: [
      { label: "Dashboard", to: "", icon: "home" },
      { label: "Notifications", to: "notifications", icon: "bell" },
      { label: "Profile", to: "profile", icon: "users" },
      {
        label: "Pay Slip",
        icon: "wallet",
        children: [
          { label: "Overview", to: "pay-slip" },
          { label: "History", to: "pay-slip/history" },
        ],
      },
      {
        label: "Imprests",
        icon: "wallet",
        children: [
          { label: "Overview", to: "imprests" },
          { label: "History", to: "imprests/history" },
        ],
      },
      {
        label: "Petty Cash",
        icon: "wallet",
        children: [
          { label: "Overview", to: "petty-cash" },
          { label: "History", to: "petty-cash/history" },
        ],
      },
      {
        label: "Handover",
        icon: "workflow",
        children: [
          { label: "Overview", to: "handover" },
          { label: "History", to: "handover/history" },
        ],
      },
      {
        label: "Performance",
        icon: "chart",
        children: [
          { label: "Overview", to: "performance" },
          { label: "History", to: "performance/history" },
        ],
      },
      {
        label: "Training and Development",
        icon: "image",
        children: [
          { label: "Overview", to: "training" },
          { label: "History", to: "training/history" },
        ],
      },
      {
        label: "Disciplinary",
        icon: "approval",
        children: [
          { label: "Overview", to: "disciplinary" },
          { label: "History", to: "disciplinary/history" },
        ],
      },
      {
        label: "Leaves",
        icon: "clock",
        children: [
          { label: "Overview", to: "leaves" },
          { label: "History", to: "leaves/history" },
        ],
      },
      {
        label: "Timesheets",
        icon: "clock",
        children: [
          { label: "Overview", to: "timesheets" },
          { label: "History", to: "timesheets/history" },
        ],
      },
    ],
    routes: [
      { index: true, element: <SelfServiceDashboard /> },
      { path: "notifications", element: <NotificationsPage /> },
      { path: "profile", element: <SelfServiceSectionPage title="Profile" /> },
      {
        path: "pay-slip",
        element: <SelfServiceSectionPage title="Pay Slip" />,
      },
      {
        path: "pay-slip/history",
        element: <SelfServiceSectionPage title="Pay Slip – History" />,
      },
      {
        path: "imprests",
        element: <SelfServiceSectionPage title="Imprests" />,
      },
      {
        path: "imprests/history",
        element: <SelfServiceSectionPage title="Imprests – History" />,
      },
      {
        path: "petty-cash",
        element: <SelfServiceSectionPage title="Petty Cash" />,
      },
      {
        path: "petty-cash/history",
        element: <SelfServiceSectionPage title="Petty Cash – History" />,
      },
      {
        path: "handover",
        element: <SelfServiceSectionPage title="Handover" />,
      },
      {
        path: "handover/history",
        element: <SelfServiceSectionPage title="Handover – History" />,
      },
      {
        path: "performance",
        element: <SelfServicePerformanceOverview />,
      },
      {
        path: "performance/smart-plans/new",
        element: <SelfServiceSmartPlanPage />,
      },
      {
        path: "performance/smart-plans/:id",
        element: <SelfServiceSmartPlanPage />,
      },
      {
        path: "performance/history",
        element: <SelfServiceSectionPage title="Performance – History" />,
      },
      {
        path: "training",
        element: <SelfServiceSectionPage title="Training and Development" />,
      },
      {
        path: "training/history",
        element: (
          <SelfServiceSectionPage title="Training and Development – History" />
        ),
      },
      {
        path: "disciplinary",
        element: <SelfServiceSectionPage title="Disciplinary" />,
      },
      {
        path: "disciplinary/history",
        element: <SelfServiceSectionPage title="Disciplinary – History" />,
      },
      { path: "leaves", element: <SelfServiceLeavesOverview /> },
      {
        path: "leaves/history",
        element: <SelfServiceLeavesHistory />,
      },
      {
        path: "timesheets",
        element: <SelfServiceSectionPage title="Timesheets" />,
      },
      {
        path: "timesheets/history",
        element: <SelfServiceSectionPage title="Timesheets – History" />,
      },
    ],
  },

  helpdesk: {
    id: 'helpdesk',
    name: 'Helpdesk',
    description: 'Support tickets',
    menu: [
      { label: "Dashboard", to: "", icon: 'home' },
      { label: 'Tickets', to: "tickets", icon: 'inbox' }
    ],
    routes: [
      { index: true, element: <HelpDeskDashboard /> },
      { path: "dashboard", element: <HelpdeskTicketsPage /> },
      { path: "tickets", element: <HelpdeskTicketsPage /> },
    ],
  },
  hrm: {
    id: "hrm",
    name: "HRM",
    description: "HRM",
    menu: [
      { label: "Dashboard", to: "", icon: "home" },

      // { label: "My Profile", to: "my-profile", icon: "user" },
      // { label: "User Profiles", to: "user-profiles", icon: "id-card" },
      { label: "Organogram", to: "organogram", icon: "network" },
      { type: "section", label: "Employees Management" },
      { label: "Employee Contracts", to: "contracts", icon: "file-signature" },
      { label: "Employees Profiles", to: "employees", icon: "employees" },
      { label: "Employee Files", to: "employee-files", icon: "folder" },
      { label: "Training Planning", to: "training-planning", icon: "calendar" },
      { label: "Promotions", to: "promotions", icon: "trending-up" },
      { label: "Resignations", to: "resignations", icon: "user-minus" },
      { label: "Terminations", to: "terminations", icon: "user-x" },
      { label: "Missing Documents", to: "missing-documents", icon: "alert-triangle" },

      { type: "section", label: "Leave Management" },
      { label: "Overview", to: "leave-management", icon: "calendar" },
      { label: "Leave Planning", to: "leave-planning", icon: "calendar" },

      { type: "section", label: "Performance Management" },
      { label: 'Performance Dashboard', to: 'performance-dashboard', icon: 'chart-column' },
      { label: "Performance Goals", to: "performance-goals", icon: "trending-up" },
      { label: "Evaluations", to: "evaluations", icon: "scale" },
      { label: "Evaluation Setup", to: "evaluation-setup", icon: "settings" },
      // { label: "Positions", to: "positions", icon: "briefcase" },

      { type: "section", label: "Settings" },
      { label: "Leave (Leave Settings)", to: "hr-leave-settings", icon: "settings" },
      { label: "General Settings", to: "hr-settings", icon: "settings" },
      { label: "Performance Management", to: "hr-performance-settings", icon: "settings" },
    ],
    routes: [
      { index: true, element: <HrmDashboard /> },
      { path: 'performance-dashboard', element: <PerformanceManagementDashboard /> },
      // { path: "my-profile", element: <MyProfilePage /> },
      // { path: "user-profiles", element: <UserProfilesPage /> },
      // { path: "user-profiles/:userId", element: <UserProfileDetailsPage /> },
      { path: "employees", element: <EmployeeProfilesPage /> },
      { path: "employees/:id", element: <EmployeeProfileDetailsPage /> },
      { path: "employees/:id/contract", element: <EmployeeContractPage /> },
      { path: "employees/:id/dependants", element: <EmployeeDependantsPage /> },
      { path: "promotions", element: <PromotionsPage /> },
      { path: "resignations", element: <ResignationsPage /> },
      { path: "terminations", element: <TerminationsPage /> },
      { path: "contracts", element: <ContractsPageHrm /> },
      { path: "contracts/new", element: <NewContractPage /> },
      { path: "employee-files", element: <EmployeeFilesPage /> },
      { path: "missing-documents", element: <EmployeeMissingDocumentsReportPage /> },
      { path: "leave-planning", element: <LeavePlanningPage /> },
      { path: "leave-management", element: <LeaveManagementPage /> },
      { path: "performance-goals", element: <PerformanceGoalsPage /> },
      { path: "evaluations", element: <EvaluationsPage /> },
      { path: "evaluations/smart-plans/new", element: <EvaluationSmartPlanPage /> },
      { path: "evaluations/smart-plans/:id", element: <EvaluationSmartPlanPage /> },
      { path: "evaluations/print/:id", element: <EvaluationPrintPage /> },
      { path: "evaluations/smart-plans/print/:id", element: <EvaluationSmartPlanPrintPage /> },
      { path: "evaluations/monthly-checkins/print/:id", element: <EvaluationMonthlyCheckinPrintPage /> },
      { path: "evaluations/appraisal-forms/print/:id", element: <EvaluationAppraisalFormPrintPage /> },
      { path: "evaluations/pip-forms/print/:id", element: <EvaluationPipFormPrintPage /> },
      { path: "evaluations/360-feedback/print/:id", element: <Evaluation360FeedbackPrintPage /> },
      { path: "evaluations/appeal-forms/print/:id", element: <EvaluationAppealFormPrintPage /> },
      { path: "evaluation-setup", element: <EvaluationSetupPage /> },
      { path: "training-planning", element: <TrainingPlanningPage /> },
      // { path: "positions", element: <PositionsPage /> },
      { path: "organogram", element: <OrganogramPage /> },
      { path: "hr-leave-settings", element: <HRLeaveSettingsTab /> },
      { path: "hr-settings", element: <HRSettingsTab /> },
      { path: "hr-performance-settings", element: <HRPerformanceManagementSettingsTab /> },
    ],
  },
  compliance: {
    id: "compliance",
    name: "Compliance",
    description: "Compliance",
    
    menu: [
      { label: 'Dashboard', to: '', icon: 'home' },
      { label: 'Deadlines & Schedules', to: 'deadlines', icon: 'clock' },
      { label: 'Standards', to: 'standards', icon: 'settings' },
      { label: 'Policies', to: 'policies', icon: 'settings' },
   
    ],
    routes: [
      { index: true, element: <ComplianceDashboard /> },
      { path: "dashboard", element: <ComplianceDashboard /> },
      { path: "deadlines", element: <ComplianceSectionPage title="Deadlines" /> },
      { path: "policies", element: <ComplianceSectionPage title="Policies" /> },
    ],
  },
  'diary': {
    id: 'diary',
    name: 'Diary',
    description: 'Personal diary and notes',
    menu: [
      { label: 'Dashboard', to: '', icon: 'home' },
      { label: 'Diary', to: 'diary', icon: 'book' },
      { label: 'Staff Diaries', to: 'staff-diaries', icon: 'users' },
      { label: 'Empowerment Session', to: 'meeting', icon: 'clock' },
      { label: 'Meeting Minutes', to: 'minutes-tool', icon: 'file-text' },
    ],
    routes: [
      { index: true, element: <MyDiaryDashboard /> },
      { path: 'dashboard', element: <MyDiaryDashboard /> },
      { path: 'diary', element: <MyDiaryPage /> },
      { path: 'staff-diaries', element: <StaffDiariesPage /> },
      { path: 'meeting', element: <MeetingPage /> },
      { path: 'minutes-tool', element: <MinutesToolPage /> },
    ],
  },

  'documents-management': {
    id: "documents-management",
    name: "EDMS",
    description: "Electronic Documents Management",
    menu: [
      { label: "Dashboard", to: "", icon: "home" },
      { label: 'Archive', to: 'archive', icon: 'folder-tree' },
      { label: 'Documents', to: 'documents', icon: 'file-stack' },
      { label: 'Files', to: 'folders', icon: 'folders' },
      { label: 'Filing', to: 'filing', icon: 'settings' },
      // { label: 'Settings', to: 'edms-settings', icon: 'settings' },
    ],
    routes: [
      { index: true, element: <EdmsDashboard /> },
      { path: "archive", element: <EdmsArchive /> },
      { path: "documents", element: <EdmsDocuments /> },
      { path: "folders", element: <EdmsFolders /> },
      { path: "filing", element: <EdmsFiling /> },
      // { path: "edms-settings", element: <EdmsSettingTabs /> },
    ],
  },
  administration: {
    id: "administration",
    name: "Administration",
    description: "Administration",
    menu: [
      { label: 'Audit Log', to: '', icon: 'chart' },
      { label: 'Workflow Settings', to: 'workflow-settings', icon: 'workflow' },
      { label: 'Forms Configurations', to: 'forms-configurations', icon: 'forms' },
      { label: 'Demographics Settings', to: 'demographics-settings', icon: 'map-pinned' },
      { label: 'Persona Settings', to: 'persona-settings', icon: 'user-cog' },
      { label: 'Project Settings', to: 'project-settings', icon: 'square-dashed-kanban' },
      { label: 'Indicator Settings', to: 'indicator-settings', icon: 'gauge' },
      { label: 'Risks Settings', to: 'risks-settings', icon: 'octagon-alert' },
      { label: 'Finance & Procurement', to: 'finance-settings', icon: 'wallet' },
      { label: 'Media Settings', to: 'media-settings', icon: 'media' },
      { label: 'Research Settings', to: 'research-settings', icon: 'research' },
      { label: 'Feedback Settings', to: 'feedback-settings', icon: 'settings' },
      { label: 'Members Settings', to: 'members-settings', icon: 'user-cog' },
      {
        label: 'User Management',
        to: 'user-management',
        icon: 'users',
        // requiredRoles: ['admin'],
        // requiredPermissions: ['assets:update'],
      },
      { label: 'EDMS Settings', to: 'documents-management', icon: 'database-dashed-kanban' },
      { label: 'General Settings', to: 'general-settings', icon: 'earth' },
      { label: 'Helpdesk Settings', to: 'helpdesk-settings', icon: 'settings' },
    ],
    routes: [
      { index: true, element: <AuditLog /> },
      { path: "audit-log", element: <AuditLog /> },
      { path: "workflow-settings", element: <WorkflowSettingsTabs /> },
      { path: "forms-configurations", element: <FormsConfigurations /> },
      { path: "demographics-settings", element: <DemographicsSettingsTabs /> },
      { path: "persona-settings", element: <PersonaSettingsTabs /> },
      { path: "project-settings", element: <ProjectSettingsTabs /> },
      { path: "indicator-settings", element: <IndicatorSettingsTabs /> },
      { path: "risks-settings", element: <RisksSettingsTabs /> },
      { path: "finance-settings", element: <FinanceSettingsTabs /> },
      { path: "media-settings", element: <MediaSettingsTabs /> },
      { path: "research-settings", element: <ResearchSettingsTabs /> },
      { path: "feedback-settings", element: <FeedbackSettingsTabs /> },
      { path: "members-settings", element: <MembersSettingsTabs /> },
      { path: "user-management", element: <UsersManagementTabs /> },
      { path: "documents-management", element: <EdmsSettingsTabs /> },
      { path: "general-settings", element: <GeneralSettingsTabs /> },
      { path: "helpdesk-settings", element: <AdminHelpdeskSettingsPage /> },
    ],
  },
};
